type ApiErrorPayload = {
  error?: unknown;
  code?: unknown;
  detail?: unknown;
};

export class ApiClientError extends Error {
  status: number;
  code: string | null;
  detail: unknown;
  payload: unknown;

  constructor(
    message: string,
    {
      status,
      code = null,
      detail = null,
      payload = null,
    }: {
      status: number;
      code?: string | null;
      detail?: unknown;
      payload?: unknown;
    },
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    this.payload = payload;
  }
}

function isErrorPayload(value: unknown): value is ApiErrorPayload {
  return value !== null && typeof value === 'object';
}

export async function readJsonSafely<T = unknown>(
  response: Response,
): Promise<T | Record<string, never>> {
  try {
    return (await response.json()) as T;
  } catch {
    return {};
  }
}

export async function throwIfNotOk(
  response: Response,
  fallbackMessage: string,
): Promise<void> {
  if (response.ok) return;

  const payload = await readJsonSafely<ApiErrorPayload>(response);
  const message =
    isErrorPayload(payload) && typeof payload.error === 'string'
      ? payload.error
      : `${fallbackMessage} (${response.status})`;

  throw new ApiClientError(message, {
    status: response.status,
    code:
      isErrorPayload(payload) && typeof payload.code === 'string'
        ? payload.code
        : null,
    detail: isErrorPayload(payload) ? payload.detail : null,
    payload,
  });
}

export async function requestJson<T>(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  fallbackMessage: string,
): Promise<T> {
  const response = await fetch(input, init);
  await throwIfNotOk(response, fallbackMessage);
  return (await readJsonSafely<T>(response)) as T;
}

export async function postJson<T>(
  input: RequestInfo | URL,
  body: unknown,
  fallbackMessage: string,
  init?: Omit<RequestInit, 'body' | 'method'>,
): Promise<T> {
  return requestJson<T>(
    input,
    {
      ...init,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers,
      },
      body: JSON.stringify(body),
    },
    fallbackMessage,
  );
}

export function testClerkUserHeaders(
  clerkUserId: string | null | undefined,
): HeadersInit | undefined {
  if (!isTestClerkUserId(clerkUserId)) return undefined;
  return { 'X-Test-Clerk-User': clerkUserId };
}

export function isTestClerkUserId(
  clerkUserId: string | null | undefined,
): clerkUserId is string {
  return (
    import.meta.env.DEV &&
    import.meta.env.VITE_FRAGMENTS_TEST_MODE === '1' &&
    Boolean(clerkUserId?.startsWith('test_user_'))
  );
}

export async function postJsonResponse(
  input: RequestInfo | URL,
  body: unknown,
  fallbackMessage: string,
  init?: Omit<RequestInit, 'body' | 'method'>,
): Promise<Response> {
  const response = await fetch(input, {
    ...init,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
    body: JSON.stringify(body),
  });
  await throwIfNotOk(response, fallbackMessage);
  return response;
}

export async function readNdjsonResponse<T>(
  response: Response,
  onMessage: (message: T) => void,
  options?: {
    shouldContinue?: () => boolean;
  },
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response stream');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    if (options?.shouldContinue && !options.shouldContinue()) {
      await reader.cancel();
      return;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.trim()) continue;
      onMessage(JSON.parse(line) as T);
    }
  }

  const tail = buffer.trim();
  if (tail) {
    onMessage(JSON.parse(tail) as T);
  }
}
